// src\components\Admin\SellerManagement\PaymentsTab\SellerBillingInfo.jsx
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader } from "../../../../components/ui/card";
import { Badge } from "../../../../components/ui/badge";
import { getSellerBillingDetails } from '../../../../services/AdminApis';
import { CheckCircle2, AlertCircle, FileText } from 'lucide-react';

const SellerBillingInfo = ({ sellerProfileId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const result = await getSellerBillingDetails(sellerProfileId);
                setData(result);
            } catch (err) {
                setError("Failed to load billing details.");
            } finally {
                setLoading(false);
            }
        };

        if (sellerProfileId) fetchData();
    }, [sellerProfileId]);

    if (loading) return <div className="p-4 text-sm text-muted-foreground">Loading billing details...</div>;
    if (error) return <div className="p-4 text-sm text-destructive">{error}</div>;

    // Helper to determine Badge color based on Verification Status
    const getStatusBadge = (status) => {
        switch (status) {
            case 'VERIFIED':
                return <Badge className="bg-green-600 hover:bg-green-700"><CheckCircle2 className="w-3 h-3 mr-1"/> Verified</Badge>;
            case 'FAILED':
                return <Badge variant="destructive"><AlertCircle className="w-3 h-3 mr-1"/> Verification Failed</Badge>;
            case 'NOT_APPLICABLE':
                return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Non-GST / Consumer</Badge>;
            case 'NOT_PROVIDED':
                return <Badge variant="secondary">Not Provided</Badge>;
            default: // PENDING or NOT_VERIFIED
                return <Badge variant="outline" className="text-yellow-600 border-yellow-600">Pending Verification</Badge>;
        }
    };

    // FIX: Only return early if data is null. 
    // We REMOVED "!data.is_gst_registered" because a seller can have billing details without GST.
    if (!data) {
        return (
            <div className="p-4 border border-dashed rounded-lg bg-slate-50 text-slate-500 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                <span>No billing information found for this seller.</span>
            </div>
        );
    }

    return (
        <Card className="border-0 shadow-none">
            <CardHeader className="px-0 pt-0 pb-4">
                <div className="flex items-center justify-between">
                    {/*<CardTitle className="text-base font-semibold flex items-center gap-2">
                        <Building2 className="w-4 h-4" /> GST & Billing Information
                    </CardTitle>*/}
                    {getStatusBadge(data.verification_status)}
                </div>
            </CardHeader>
            <CardContent className="px-0 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-2 text-sm">
                        <span className="font-medium text-muted-foreground">Legal Name:</span>
                        <span className="col-span-2 font-medium">{data.legal_name || 'N/A'}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                        <span className="font-medium text-muted-foreground">GSTIN:</span>
                        <span className="col-span-2">
                            {data.gstin ? (
                                <span className="font-mono bg-slate-100 px-2 py-0.5 rounded w-fit text-xs">
                                    {data.gstin}
                                </span>
                            ) : (
                                <span className="text-muted-foreground italic">Not Applicable</span>
                            )}
                        </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                        <span className="font-medium text-muted-foreground">Place of Supply:</span>
                        <span className="col-span-2">{data.place_of_supply || 'N/A'}</span>
                    </div>
                    
                    {/* Visual indicator for GST Registration Status */}
                    {/*<div className="grid grid-cols-3 gap-2 text-sm items-center">
                        <span className="font-medium text-muted-foreground">GST Registered:</span>
                        <span className="col-span-2 flex items-center gap-2">
                            {data.is_gst_registered ? (
                                <span className="text-green-600 flex items-center text-xs font-semibold"><CheckCircle2 className="w-3 h-3 mr-1"/> Yes</span>
                            ) : (
                                <span className="text-slate-500 flex items-center text-xs"><Ban className="w-3 h-3 mr-1"/> No</span>
                            )}
                        </span>
                    </div>*/}
                </div>

                <div className="space-y-2">
                    <span className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                         <FileText className="w-4 h-4"/> Registered Address
                    </span>
                    {data.billing_address ? (
                        <div className="p-3 bg-slate-50 rounded-md text-sm text-slate-700 leading-relaxed border">
                            {/* Handle cases where fields might be named differently or combined */}
                            {data.billing_address.street && <p>{data.billing_address.street}</p>}
                            {data.billing_address.address_line_1 && <p>{data.billing_address.address_line_1}</p>}
                            {data.billing_address.address_line_2 && <p>{data.billing_address.address_line_2}</p>}
                            <p>
                                {data.billing_address.city}
                                {data.billing_address.pincode ? `, ${data.billing_address.pincode}` : ''}
                            </p>
                            <p>
                                {data.billing_address.state}
                                {data.billing_address.country ? `, ${data.billing_address.country}` : ''}
                            </p>
                        </div>
                    ) : (
                        <span className="text-sm italic text-muted-foreground">Address data unavailable</span>
                    )}
                </div>
                
                {data.updated_at && (
                    <div className="col-span-2 text-xs text-muted-foreground pt-2 border-t mt-2">
                        Last updated: {new Date(data.updated_at).toLocaleDateString()}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default SellerBillingInfo;